import { useMemo } from 'react';
import { useCompanyBetaFeatures } from 'shared/hooks';

import { useOffboardingContext } from '../../OffboardingContext';
import { getRequestReplacementId, isRequiredRoleToOffboard } from '../../OffboardUserPopupContent.helpers';
import { GLOBAL_USER_REPLACEMENT, RequestReplacementMode } from '../../OffboardUserPopupContent.types';
import { MapToTableRowsParams, RequestsProps } from './Requests.types';

export const useRequestsData = (companyId: string, requests: RequestsProps['requests'] = []) => {
    const { requestReplacementMode, selectedReplacements } = useOffboardingContext();

    const { isPayerOffboarding } = useCompanyBetaFeatures(companyId);

    return useMemo(
        () =>
            mapToTableRows({
                companyId,
                requests,
                selectedReplacements,
                requestReplacementMode,
                isPayerOffboardingBetaFeature: isPayerOffboarding,
            }),
        [companyId, isPayerOffboarding, requestReplacementMode, requests, selectedReplacements]
    );
};

const mapToTableRows = ({
    companyId,
    requests = [],
    selectedReplacements,
    requestReplacementMode,
    isPayerOffboardingBetaFeature,
}: MapToTableRowsParams) => {
    const oneForAllReplacement = requestReplacementMode === RequestReplacementMode.OneForAll;

    return requests.flatMap((request) => {
        return request.replacements.map((replacement) => {
            let required = false;

            if (isPayerOffboardingBetaFeature) {
                required = replacement.isReplacementRequired;
            } else {
                if (request.hasOnlyApprover) {
                    required = true;
                }

                if (isRequiredRoleToOffboard(replacement.role)) {
                    const replacementId = getRequestReplacementId(replacement, request);
                    const requiredReplacementFilled = oneForAllReplacement
                        ? selectedReplacements[GLOBAL_USER_REPLACEMENT]
                        : selectedReplacements[replacementId];

                    if (!requiredReplacementFilled) {
                        required = true;
                    }
                }
            }

            return {
                id: getRequestReplacementId(replacement, request),
                requestId: request.requestId,
                name: request.name,
                amount: request.amount,
                currency: request.currency,
                step: replacement.stepName,
                companyId,
                required,
            };
        });
    });
};
